import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Trash2, Download, Eye, Wind, Wine as WineIcon, FileText, Award, User } from "lucide-react";
import { categoriaPuntuacion, AROMAS } from "@/lib/tastingOptions";
import { toast } from "sonner";
import SommChat from "@/components/SommChat";
import { buildTastingContext } from "@/lib/tastingContext";
import ReactionBar from "@/components/ReactionBar";

const val = (v) => {
  if (v == null || v === "") return null;
  if (Array.isArray(v)) return v.length ? v.join(", ") : null;
  return String(v);
};

function Row({ label, value }) {
  const v = val(value);
  if (!v) return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-grises-100 last:border-0">
      <span className="text-sm text-grises-500">{label}</span>
      <span className="text-sm text-negro font-medium text-right">{v}</span>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  const hasContent = React.Children.toArray(children).some((c) => c);
  return (
    <Card className="p-6 border-grises-200 rounded-xl">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-borgona-700" strokeWidth={1.75} />
        <h3 className="font-serif text-xl text-negro">{title}</h3>
      </div>
      <div>{hasContent ? children : <p className="text-sm text-grises-400">Sin datos registrados</p>}</div>
    </Card>
  );
}

export default function TastingDetail({ community = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmDel, setConfirmDel] = useState(false);

  const { data: t, isLoading } = useQuery({
    queryKey: [community ? "community-tasting" : "tasting", id],
    queryFn: async () => (await api.get(`${community ? "/community/tastings" : "/tastings"}/${id}`)).data,
  });

  if (isLoading || !t) return <div className="text-grises-400">Cargando ficha...</div>;

  const w = t.wine || {};
  const cat = categoriaPuntuacion(t.puntuacion);
  const v = t.visual || {}, o = t.olfativo || {}, g = t.gustativo || {}, ob = t.observaciones || {};

  const del = async () => {
    try {
      await api.delete(`/tastings/${id}`);
      qc.invalidateQueries({ queryKey: ["tastings"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Cata eliminada");
      navigate("/app/historial");
    } catch { toast.error("No se pudo eliminar"); }
  };

  const exportPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const M = 48;
    let y = 56;
    const line = (yy) => { doc.setDrawColor(220); doc.line(M, yy, W - M, yy); };
    const check = (h = 16) => { if (y + h > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 56; } };

    doc.setTextColor(114, 47, 55);
    doc.setFont("times", "bold"); doc.setFontSize(26);
    doc.text("Vinonotas", M, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(180, 134, 11);
    doc.text("TUS CATAS · TUS NOTAS · TU PASIÓN", M, y + 14);
    doc.setTextColor(120); doc.setFontSize(9);
    doc.text("Ficha de Cata", W - M, y, { align: "right" });
    if (t.fecha_cata) doc.text(t.fecha_cata, W - M, y + 14, { align: "right" });
    y += 32; line(y); y += 24;

    doc.setTextColor(10); doc.setFont("times", "bold"); doc.setFontSize(18);
    doc.text(w.nombre || "Vino", M, y); y += 18;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
    doc.text([w.bodega, w.pais, w.region, w.variedad, w.anada].filter(Boolean).join("  ·  "), M, y);
    y += 8;

    // Score box
    doc.setFillColor(114, 47, 55);
    doc.roundedRect(W - M - 90, y - 44, 90, 44, 6, 6, "F");
    doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(22);
    doc.text(String(t.puntuacion), W - M - 45, y - 20, { align: "center" });
    doc.setFontSize(7);
    doc.text(cat.label.toUpperCase(), W - M - 45, y - 8, { align: "center" });
    y += 16; line(y); y += 22;

    const block = (title, rows) => {
      const valid = rows.filter((r) => val(r[1]));
      if (!valid.length) return;
      check(28);
      doc.setFont("times", "bold"); doc.setFontSize(13); doc.setTextColor(114, 47, 55);
      doc.text(title, M, y); y += 16;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      valid.forEach(([k, vv]) => {
        check(16);
        doc.setTextColor(120); doc.text(k, M, y);
        doc.setTextColor(20);
        const text = doc.splitTextToSize(val(vv), W - M - 200);
        doc.text(text, W - M, y, { align: "right" });
        y += 14 * text.length + 2;
      });
      y += 10;
    };

    block("Datos Generales", [
      ["Tipo", w.tipo], ["Denominación", w.denominacion], ["Alcohol", w.alcohol && `${w.alcohol}%`],
      ["Precio", w.precio], ["Lugar de compra", w.lugar_compra],
    ]);
    block("Examen Visual", [
      ["Limpidez", v.limpidez], ["Brillantez", v.brillantez], ["Color", v.color],
      ["Intensidad", v.intensidad], ["Densidad", v.densidad], ["Sedimentos", v.sedimentos],
      ["Observaciones", v.observaciones],
    ]);
    const aromas = Object.keys(AROMAS).flatMap((f) => o[`aromas_${f}`] || []);
    block("Examen Olfativo", [
      ["Estado", o.estado], ["Limpieza", o.limpieza], ["Primera impresión", o.primera_impresion],
      ["Intensidad", o.intensidad], ["Desarrollo", o.desarrollo], ["Carácter", o.caracter], ["Defectos", o.defectos],
      ["Barrica", o.barrica], ["Tipo barrica", o.barrica_tipo], ["Tipo de aromas", o.tipo_aromas], ["Aromas", aromas],
    ]);
    block("Examen Gustativo", [
      ["Dulzor", g.dulzor], ["Equilibrio", g.equilibrio], ["Acidez", g.acidez],
      ["Alcohol", g.alcohol], ["Taninos", g.taninos_cantidad], ["Astringencia", g.astringencia],
      ["Cuerpo", g.cuerpo], ["Intensidad de sabor", g.intensidad_sabor], ["Final (longitud)", g.final],
      ["Persistencia", g.persistencia], ["Estilo", g.estilo],
      ["Decantar", g.decantar], ["Temperatura", g.temperatura], ["Maridaje", g.maridaje],
    ]);
    block("Conclusión", [
      ["Nivel de calidad", ob.calidad], ["Consumo / guarda", ob.consumo], ["Clima estimado", ob.clima],
      ["Mundo / Estilo", ob.mundo], ["Variedad probable", ob.variedad_probable],
      ["País / Región probable", ob.origen_probable], ["Añada estimada", ob.anada_probable],
    ]);
    block("Observaciones", [
      ["Notas", ob.notas], ["Evolución", ob.evolucion], ["Momento ideal", ob.momento],
      ["Calidad-precio", ob.relacion_precio], ["Recomendaciones", ob.recomendaciones],
      ["¿Volvería a comprar?", ob.volveria], ["Etiquetas", ob.etiquetas],
    ]);

    doc.save(`Vinonotas-${(w.nombre || "cata").replace(/\s+/g, "-")}.pdf`);
    toast.success("PDF generado");
  };

  const aromasList = Object.keys(AROMAS).flatMap((f) => o[`aromas_${f}`] || []);

  return (
    <div className="max-w-4xl mx-auto animate-fade-up">
      <button data-testid="detail-back-btn" onClick={() => navigate(community ? "/app/comunidad" : "/app/historial")} className="flex items-center gap-1.5 text-sm text-grises-500 hover:text-borgona-700 mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> {community ? "Volver a Catas Compartidas" : "Volver al historial"}
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-6 mb-6">
        {w.foto_botella && (
          <img src={fileUrl(w.foto_botella)} alt={w.nombre} className="w-full md:w-40 h-52 object-cover rounded-xl border border-grises-200" />
        )}
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              {t.status === "borrador" && <span className="text-xs px-2 py-0.5 rounded-full bg-grises-100 text-grises-500 mb-2 inline-block">Borrador</span>}
              <h1 className="font-serif text-4xl text-negro leading-tight">{w.nombre}</h1>
              <p className="text-grises-500 mt-1">{[w.bodega, w.pais, w.region].filter(Boolean).join(" · ")}</p>
              <p className="text-sm text-grises-400">{[w.variedad, w.anada, w.tipo].filter(Boolean).join(" · ")}</p>
              {t.fecha_cata && <p data-testid="detail-fecha" className="text-sm text-grises-500 mt-2">Catado el {t.fecha_cata}</p>}
              {community && t.author && (
                <p data-testid="detail-author" className="text-sm text-borgona-700 mt-1 flex items-center gap-1.5">
                  <User className="w-4 h-4" /> Compartido por {t.author}
                </p>
              )}
            </div>
            <div className="text-center shrink-0">
              <div className="font-mono text-5xl font-bold" style={{ color: cat.color }} data-testid="detail-score">{t.puntuacion}</div>
              <div className="text-xs uppercase tracking-wider font-medium" style={{ color: cat.color }}>{cat.label}</div>
            </div>
          </div>
          {community ? (
            <div className="mt-5">
              <ReactionBar tastingId={t.id} reactions={t.reactions} />
            </div>
          ) : (
          <div className="flex flex-wrap gap-2 mt-5">
            <Button data-testid="export-pdf-btn" onClick={exportPDF} className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full transition-colors duration-300">
              <Download className="w-4 h-4 mr-1" /> Exportar PDF
            </Button>
            <Button data-testid="edit-tasting-btn" variant="outline" onClick={() => navigate(`/app/catas/${id}/editar`)} className="rounded-full border-grises-300">
              <Pencil className="w-4 h-4 mr-1" /> Editar
            </Button>
            <Button data-testid="delete-tasting-btn" variant="outline" onClick={() => setConfirmDel(true)} className="rounded-full border-grises-300 text-destructive hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar
            </Button>
          </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section icon={Eye} title="Examen Visual">
          <Row label="Limpidez" value={v.limpidez} />
          <Row label="Brillantez" value={v.brillantez} />
          <Row label="Tipo" value={v.tipo} />
          <Row label="Color" value={v.color} />
          <Row label="Intensidad" value={v.intensidad} />
          <Row label="Ribete" value={[v.ribete_color, v.ribete_anchura, v.ribete_definicion].filter(Boolean).join(" / ")} />
          <Row label="Densidad" value={v.densidad} />
          <Row label="Lágrimas" value={[v.lagrimas_cantidad, v.lagrimas_velocidad, v.lagrimas_grosor].filter(Boolean).join(" / ")} />
          <Row label="Gas carbónico" value={v.gas_presencia} />
          <Row label="Sedimentos" value={v.sedimentos} />
          <Row label="Observaciones" value={v.observaciones} />
        </Section>

        <Section icon={Wind} title="Examen Olfativo">
          <Row label="Estado" value={o.estado} />
          <Row label="Limpieza" value={o.limpieza} />
          <Row label="Primera impresión" value={o.primera_impresion} />
          <Row label="Intensidad" value={o.intensidad} />
          <Row label="Desarrollo / Evolución" value={o.desarrollo} />
          <Row label="Carácter aromático" value={o.caracter} />
          <Row label="Defectos" value={o.defectos} />
          <Row label="Barrica" value={o.barrica} />
          <Row label="Tipo barrica" value={o.barrica_tipo} />
          <Row label="Tostado" value={o.tostado} />
          <Row label="Tipo de aromas" value={o.tipo_aromas} />
          <Row label="Aromas" value={aromasList} />
        </Section>

        <Section icon={WineIcon} title="Examen Gustativo">
          <Row label="Dulzor" value={g.dulzor} />
          <Row label="Primera impresión" value={g.primera_impresion} />
          <Row label="Equilibrio" value={g.equilibrio} />
          <Row label="Acidez" value={g.acidez} />
          <Row label="Alcohol" value={g.alcohol} />
          <Row label="Taninos" value={[g.taninos_cantidad, g.taninos_calidad].filter(Boolean).join(" / ")} />
          <Row label="Astringencia" value={g.astringencia} />
          <Row label="Cuerpo" value={g.cuerpo} />
          <Row label="Intensidad de sabor" value={g.intensidad_sabor} />
          <Row label="Final (longitud)" value={g.final} />
          <Row label="Persistencia (caudalías)" value={g.persistencia} />
          <Row label="Estado" value={g.estado} />
          <Row label="Estilo" value={g.estilo} />
          <Row label="Potencial de guarda" value={g.potencial_guarda} />
          <Row label="Decantar" value={g.decantar} />
          <Row label="Temperatura" value={g.temperatura} />
          <Row label="Maridaje" value={g.maridaje} />
        </Section>

        <Section icon={Award} title="Conclusión">
          <Row label="Nivel de calidad" value={ob.calidad} />
          <Row label="Consumo / guarda" value={ob.consumo} />
          <Row label="Clima estimado" value={ob.clima} />
          <Row label="Mundo / Estilo" value={ob.mundo} />
          <Row label="Variedad probable" value={ob.variedad_probable} />
          <Row label="País / Región probable" value={ob.origen_probable} />
          <Row label="Añada estimada" value={ob.anada_probable} />
        </Section>

        <Section icon={FileText} title="Observaciones">
          <Row label="Notas" value={ob.notas} />
          <Row label="Evolución" value={ob.evolucion} />
          <Row label="Momento ideal" value={ob.momento} />
          <Row label="Calidad-precio" value={ob.relacion_precio} />
          <Row label="Recomendaciones" value={ob.recomendaciones} />
          <Row label="¿Volvería a comprar?" value={ob.volveria} />
          <Row label="Etiquetas" value={ob.etiquetas} />
        </Section>
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">¿Eliminar cata?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-tasting-btn" onClick={del} className="bg-destructive hover:bg-destructive/90 rounded-full">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {!community && <SommChat sessionId={id} getContext={() => buildTastingContext(w, t)} />}
    </div>
  );
}
